import React from "react";
import { Card } from "@/components/ui/card";
import { FileText, Users, AlertCircle, ArrowRight, Wallet } from "lucide-react";

export default function HomeTab({ owner, stats, receipts, onNavigate }) {
  const collected = stats?.collected_this_month || 0;
  const pending = stats?.pending_this_month || 0;
  const expected = collected + pending;
  const totalBeds = owner?.total_beds || 0;
  const occupied = Math.max(totalBeds - (owner?.available_beds || 0), 0);

  return (
    <div className="space-y-6" data-testid="home-tab">
      <div>
        <h2 className="font-display text-2xl font-semibold text-slate-900">Welcome back, {owner?.property_name}</h2>
        <p className="text-sm text-slate-500 mt-1">Here's what's happening at your property in {stats?.current_month}.</p>
      </div>

      {/* Visual overview */}
      <Card className="p-6 border-slate-200" data-testid="home-visuals">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-6">
          <MiniStat icon={<Wallet className="h-5 w-5" />} label={`Collected in ${stats?.current_month || ""}`} value={`₹ ${collected.toLocaleString("en-IN")}`} color="#059669" testid="home-collected" />
          <MiniStat icon={<AlertCircle className="h-5 w-5" />} label="Yet to collect" value={`₹ ${pending.toLocaleString("en-IN")}`} color="#F59E0B" testid="home-yet-to-collect" />
          <MiniStat icon={<Users className="h-5 w-5" />} label="Active tenants" value={stats?.active_tenants ?? "—"} color="#2563EB" testid="home-active-tenants" />
        </div>
        <div className="space-y-5">
          <Meter label={`Collection progress — ${stats?.current_month || ""}`} value={collected} max={expected || 1} color="#059669" display={`₹ ${collected.toLocaleString("en-IN")} of ₹ ${expected.toLocaleString("en-IN")}`} testid="home-collection-meter" />
          {totalBeds > 0 && (
            <Meter label="Bed occupancy" value={occupied} max={totalBeds} color="#7C3AED" display={`${occupied} of ${totalBeds} beds occupied`} testid="home-occupancy-meter" />
          )}
        </div>
      </Card>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <QuickAction title="Generate Receipt" desc="Create & share a rent receipt in seconds" icon={<FileText className="h-5 w-5" />} color="#2563EB" onClick={() => onNavigate("generate")} testid="home-generate-receipt-btn" />
        <QuickAction title="Add Tenant" desc="Register a new tenant at your property" icon={<Users className="h-5 w-5" />} color="#7C3AED" onNavigate={() => {}} onClick={() => onNavigate("tenants")} testid="home-add-tenant-btn" />
        <QuickAction
          title="Pending Payments"
          desc={`₹ ${pending.toLocaleString("en-IN")} pending this month`}
          icon={<AlertCircle className="h-5 w-5" />} color="#F59E0B" onClick={() => onNavigate("pending")} testid="home-pending-btn"
        />
      </div>

      <Card className="p-6 border-slate-200">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-lg font-semibold">Recent receipts</h3>
          <button className="text-sm text-indigo-600 hover:underline flex items-center gap-1" onClick={() => onNavigate("downloads")} data-testid="home-view-all-receipts">
            View all <ArrowRight className="h-3.5 w-3.5" />
          </button>
        </div>
        {receipts.length === 0 ? (
          <p className="text-sm text-slate-500 py-6 text-center">No receipts yet — generate your first one.</p>
        ) : (
          <div className="divide-y divide-slate-100">
            {receipts.slice(0, 5).map((r) => (
              <div key={r.id} className="py-3 flex items-center justify-between text-sm">
                <div>
                  <span className="font-mono text-xs text-indigo-700 mr-3">{r.receipt_no}</span>
                  <span className="font-medium">{r.tenant_name}</span>
                  <span className="text-slate-400 text-xs ml-2">Room {r.room_no} • {r.month_year}</span>
                </div>
                <span className="font-semibold text-emerald-700">₹ {Number(r.amount_paid).toLocaleString("en-IN")}</span>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

function MiniStat({ icon, label, value, color, testid }) {
  return (
    <div className="flex items-center gap-3" data-testid={testid}>
      <div className="h-11 w-11 rounded-xl flex items-center justify-center text-white shrink-0" style={{ background: color }}>{icon}</div>
      <div>
        <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{label}</p>
        <p className="font-display text-xl font-bold text-slate-900">{value}</p>
      </div>
    </div>
  );
}

function Meter({ label, value, max, color, display, testid }) {
  const pct = max > 0 ? Math.min(100, Math.round((value / max) * 100)) : 0;
  return (
    <div data-testid={testid}>
      <div className="flex items-center justify-between text-xs mb-1.5">
        <span className="font-semibold uppercase tracking-wider text-slate-500">{label}</span>
        <span className="font-medium text-slate-700">{display} · {pct}%</span>
      </div>
      <div className="h-2.5 rounded-full bg-slate-100 overflow-hidden">
        <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, background: color }} />
      </div>
    </div>
  );
}

function QuickAction({ title, desc, icon, color, onClick, testid }) {
  return (
    <button onClick={onClick} data-testid={testid} className="text-left rounded-xl bg-white border border-slate-200 p-5 hover:shadow-md hover:border-slate-300 transition-all">
      <div className="h-10 w-10 rounded-lg flex items-center justify-center text-white" style={{ background: color }}>{icon}</div>
      <p className="mt-3 font-display font-semibold text-slate-900">{title}</p>
      <p className="text-sm text-slate-500 mt-1">{desc}</p>
    </button>
  );
}
